export const queryKeys = {
  auth: {
    profile: () => ['auth', 'profile'] as const,
  },
  estimates: {
    all: ['estimates'] as const,
    list: (teamId: string | number) => ['estimates', 'list', String(teamId)] as const,
    detail: (estimateId: string | number) => ['estimates', 'detail', String(estimateId)] as const,
  },
  vehicles: {
    all: ['vehicles'] as const,
    // Keyed by VIN alone: the BE scopes the answer to the caller's own
    // teams, so the same VIN can't mean different things for one session.
    existingEstimates: (vin: string) => ['vehicles', 'existing', vin] as const,
  },
  teams: {
    all: ['teams'] as const,
    list: () => ['teams', 'list'] as const,
    detail: (teamId: string | number) => ['teams', 'detail', String(teamId)] as const,
    dealerships: (teamId: string | number) => ['teams', 'dealerships', String(teamId)] as const,
    connectedBodyshop: (dealershipId: string | number) => ['teams', 'connected-bodyshop', String(dealershipId)] as const,
    connectedDealerships: (bodyshopId: string | number) => ['teams', 'connected-dealerships', String(bodyshopId)] as const,
    members: (teamId: string | number) => ['teams', 'members', String(teamId)] as const,
  },
  invites: {
    all: ['invites'] as const,
    byTeam: (teamId: string | number) => ['invites', 'team', String(teamId)] as const,
    byUuid: (uuid: string) => ['invites', 'uuid', uuid] as const,
  },
  salesLeads: {
    all: ['salesLeads'] as const,
    list: (teamId: string | number, page: number) => ['salesLeads', 'list', String(teamId), page] as const,
    pending: (teamId: string | number) => ['salesLeads', 'pending', String(teamId)] as const,
    assigned: (teamId: string | number) => ['salesLeads', 'assigned', String(teamId)] as const,
    detail: (teamId: string | number, leadId: string | number) =>
      ['salesLeads', 'detail', String(teamId), String(leadId)] as const,
    search: (teamId: string | number, term: string) =>
      ['salesLeads', 'search', String(teamId), term] as const,
  },
  ivrCalls: {
    all: ['ivrCalls'] as const,
    list: (teamId: string | number, kind?: string, status?: string) =>
      ['ivrCalls', 'list', String(teamId), kind ?? 'all', status ?? 'all'] as const,
    detail: (id: string) => ['ivrCalls', 'detail', id] as const,
    transcript: (id: string) => ['ivrCalls', 'transcript', id] as const,
  },
  calendar: {
    all: ['calendar'] as const,
    events: (teamId: string | number, from: string, to: string) =>
      ['calendar', 'events', String(teamId), from, to] as const,
    workingHours: (teamId: string | number) =>
      ['calendar', 'working-hours', String(teamId)] as const,
    freeSlots: (teamId: string | number, from: string, to: string) =>
      ['calendar', 'free-slots', String(teamId), from, to] as const,
    publicCalendarId: (estimateId: string | number) =>
      ['calendar', 'public', String(estimateId)] as const,
  },
  tasks: {
    all: ['tasks'] as const,
    list: (teamId: string | number) => ['tasks', 'list', String(teamId)] as const,
    byEstimate: (estimateId: string | number) => ['tasks', 'estimate', String(estimateId)] as const,
  },
  customerActivity: {
    all: ['customerActivity'] as const,
    detail: (customerId: string | number) =>
      ['customerActivity', String(customerId)] as const,
  },
  ghl: {
    all: ['ghl'] as const,
    customerConversation: (teamId: string | number, customerId: string | number) =>
      ['ghl', 'customer-conversation', String(teamId), String(customerId)] as const,
    conversation: (teamId: string | number, conversationId: string) =>
      ['ghl', 'conversation', String(teamId), conversationId] as const,
    status: (teamId: string | number) => ['ghl', 'status', String(teamId)] as const,
  },
  desk: {
    all: ['desk'] as const,
    queue: (teamId: string | number) => ['desk', 'queue', String(teamId)] as const,
    customers: (teamId: string | number, params: string) => ['desk', 'customers', String(teamId), params] as const,
    record: (teamId: string | number, customerId: string | number) =>
      ['desk', 'record', String(teamId), String(customerId)] as const,
    /** Prefix over every page and filter of one team's customer lists. */
    customersAll: (teamId: string | number) => ['desk', 'customers', String(teamId)] as const,
    audience: (teamId: string | number, params: string) => ['desk', 'audience', String(teamId), params] as const,
    estimates: (teamId: string | number, params: string) => ['desk', 'estimates', String(teamId), params] as const,
    estimatesAll: (teamId: string | number) => ['desk', 'estimates', String(teamId)] as const,
    results: (teamId: string | number, days: number) => ['desk', 'results', String(teamId), days] as const,
  },
  analytics: {
    all: ['analytics'] as const,
    // Metrics are sorted so ['a','b'] and ['b','a'] share a cache slot; dates
    // are part of the key so each window caches separately (null = all-time).
    query: (
      teamId: string | number,
      metrics: readonly string[],
      startDate: string | null,
      endDate: string | null,
    ) => ['analytics', 'query', String(teamId), [...metrics].sort().join(','), startDate, endDate] as const,
  },
  activation: {
    all: ['activation'] as const,
    // Both lists are fetched unfiltered and filtered client-side — the BE's
    // boolean query filters are broken for `false` (see src/lib/api/activation.ts).
    // So the keys don't vary by filter: one cache entry each, no refetch on
    // tab change.
    managers: () => ['activation', 'managers'] as const,
    manager: (id: string | number) => ['activation', 'manager', String(id)] as const,
    contracts: () => ['activation', 'contracts'] as const,
    contract: (id: string | number) => ['activation', 'contract', String(id)] as const,
    // Public surface — keyed by the opaque uuid from the customer's link.
    publicContract: (uuid: string) => ['activation', 'public', uuid] as const,
  },
  reporting: {
    all: ['reporting'] as const,
    // Date params are part of the key so each window has its own cache slot.
    // null/null means "all time" — same key shape regardless so a switch back
    // to all-time hits the cache.
    estimates: (
      teamId: string | number,
      startDate: string | null,
      endDate: string | null,
    ) => ['reporting', 'estimates', String(teamId), startDate, endDate] as const,
  },
  rewards: {
    all: ['rewards'] as const,
    milestones: (teamId: string | number) => ['rewards', 'milestones', String(teamId)] as const,
    leaderboards: (teamId: string | number) => ['rewards', 'leaderboards', String(teamId)] as const,
    me: (teamId: string | number) => ['rewards', 'me', String(teamId)] as const,
    balances: (teamId: string | number) => ['rewards', 'balances', String(teamId)] as const,
    pending: (teamId: string | number) => ['rewards', 'pending', String(teamId)] as const,
    // Grouped/dated pending — `scope` folds in the date window + filters so each
    // period's data caches separately.
    pendingGrouped: (teamId: string | number, scope: string) =>
      ['rewards', 'pending-grouped', String(teamId), scope] as const,
    userDetail: (teamId: string | number, userId: string | number) =>
      ['rewards', 'user', String(teamId), String(userId)] as const,
    userSummary: (teamId: string | number, userId: string | number) =>
      ['rewards', 'user-summary', String(teamId), String(userId)] as const,
    // Analytics / payout history. `range` scopes a query to its date window so
    // changing the filter refetches rather than showing stale rows.
    analyticsSummary: (teamId: string | number, range: string) =>
      ['rewards', 'analytics', 'summary', String(teamId), range] as const,
    payouts: (teamId: string | number, range: string) =>
      ['rewards', 'analytics', 'payouts', String(teamId), range] as const,
    payoutsByDay: (teamId: string | number, range: string) =>
      ['rewards', 'analytics', 'by-day', String(teamId), range] as const,
    payoutsByAdvisor: (teamId: string | number, range: string) =>
      ['rewards', 'analytics', 'by-advisor', String(teamId), range] as const,
    advisorPayouts: (teamId: string | number, userId: string | number, range: string) =>
      ['rewards', 'analytics', 'advisor-payouts', String(teamId), String(userId), range] as const,
  },
}
